import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { setSensorLocations } from "../../State/slices/sensorLocations-slice";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import { useInstallTracking } from "Pages/Installation/InstallWrapper";
import { toSnakeCase } from "Services/Utils/toSnakeCase";

const InstallSensorLocation = ({
  isHub,
  sensor,
  showLocationError,
  onLocationChange,
  currentLocation: currentLocationProp,
}) => {
  const isEditMode = typeof onLocationChange === "function";

  const dispatch = useDispatch();
  const sensorLocations = useSelector((state) => state.sensorLocations);

  const sensorLocation = isEditMode
    ? currentLocationProp
    : sensorLocations?.[sensor?.name];

  const [pendingOther, setPendingOther] = useState(false);

  // Keep track of when the user is entering a custom appliance for the plug sensor
  const otherSelected =
    (isEditMode && pendingOther) ||
    !!(
      sensorLocation &&
      (sensorLocation === "other" ||
        !sensor?.locationOptions?.find(
          (location) => location.value === sensorLocation
        ))
    );

  // Local state for the free-text input in edit mode (avoid API call on every keystroke)
  const [localOtherInput, setLocalOtherInput] = useState(
    otherSelected && isEditMode
      ? sensorLocation === "other"
        ? ""
        : (sensorLocation ?? "")
      : ""
  );

  // Sync localOtherInput when the parent's currentLocation changes (e.g. after a successful save)
  useEffect(() => {
    if (!isEditMode) return;
    const isCustom =
      currentLocationProp &&
      currentLocationProp !== "other" &&
      !sensor?.locationOptions?.find(
        (loc) => loc.value === currentLocationProp
      );
    setLocalOtherInput(isCustom ? currentLocationProp : "");
  }, [currentLocationProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const { trackInstall } = useInstallTracking();

  // In edit mode, remove the currently saved option from the dropdown so the
  // user cannot re-select (and re-trigger a save for) the same value.
  const availableLocationOptions = isEditMode
    ? sensor?.locationOptions?.filter((opt) => {
        if (pendingOther) return true;
        if (otherSelected) return opt.value !== "other";
        return opt.value !== sensorLocation;
      })
    : sensor?.locationOptions;

  const saveSensorLocation = (location) => {
    if (isEditMode) {
      if (location.value === "other") {
        // onLocationChange won't be called when user press Other option
        setPendingOther(true);
        setLocalOtherInput("");
        return;
      }
      // location sensor is chosen (or typed into the custom input).
      setPendingOther(false);
      onLocationChange(location);
      return;
    }

    const updatedLocations = { ...sensorLocations };
    const isOther =
      location.value?.toLowerCase?.() === "other" ||
      location.value?.length === 0;

    // When the user deletes a custom location set it back to "other"
    updatedLocations[sensor?.name] =
      location.value?.length === 0 ? "other" : location.value;
    dispatch(setSensorLocations(updatedLocations));

    // Only track when user selects from the dropdown, not when typing custom input
    if (!isOther) {
      trackInstall({
        action: "sensor_location_selected",
        sensor: sensor?.label,
        location: toSnakeCase(location?.value),
      });
    } else {
      trackInstall({
        action: "sensor_location_selected",
        sensor: sensor?.label,
        location: "other",
      });
    }
  };

  return (
    <div>
      {!sensor?.locationRequired && (
        <div
          className={
            isHub
              ? styles.hub_location_placeholder
              : styles.location_placeholder
          }
        ></div>
      )}
      {sensor?.locationRequired && (
        <>
          <div className={styles.select_appliacnce}>
            <label>
              Where was this sensor placed?
              <Select
                placeholder="Choose location..."
                options={availableLocationOptions}
                value={
                  otherSelected
                    ? { value: "other", label: "Other" }
                    : sensorLocation
                      ? { value: sensorLocation, label: sensorLocation }
                      : null
                }
                onChange={saveSensorLocation}
                isSearchable={false}
                styles={{
                  control: (baseStyles, { isFocused }) => ({
                    ...baseStyles,
                    borderColor: isFocused
                      ? theme.primary3
                      : showLocationError
                        ? theme.risk4
                        : theme.neutral3,
                    marginTop: 4,
                    fontSize: theme.bodyMedium,
                    color: theme.neutral7,
                    borderRadius: 8,
                    ":hover": {
                      borderColor: showLocationError
                        ? theme.risk4
                        : theme.primary3,
                    },
                    boxShadow: "none",
                  }),
                  option: (styles, { isFocused }) => ({
                    ...styles,
                    fontSize: "16px",
                    backgroundColor: isFocused ? theme.neutral2 : null,
                    color: theme.neutral7,
                    // adjust background colour when mouse button is pressed
                    ":active": {
                      backgroundColor: theme.neutral3,
                    },
                  }),
                }}
                components={{
                  IndicatorSeparator: () => null, // Removes the "|" to the left of the drop-down arrow
                }}
              />
            </label>
          </div>
          {otherSelected && (
            <div className={styles.appliance_other}>
              <label>
                Other:
                <input
                  className={styles.input}
                  placeholder="Location"
                  onChange={(e) => {
                    if (isEditMode) {
                      // Only update local state; persist on blur/Enter
                      setLocalOtherInput(e.target.value);
                    } else {
                      saveSensorLocation({ value: e.target.value });
                    }
                  }}
                  onBlur={
                    isEditMode
                      ? () => {
                          const trimmed = localOtherInput.trim();
                          if (trimmed) {
                            saveSensorLocation({ value: trimmed });
                          }
                        }
                      : undefined
                  }
                  onKeyDown={
                    isEditMode
                      ? (e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const trimmed = localOtherInput.trim();
                            if (trimmed) {
                              saveSensorLocation({ value: trimmed });
                            }
                          }
                        }
                      : undefined
                  }
                  value={
                    isEditMode
                      ? localOtherInput
                      : sensorLocation === "other"
                        ? ""
                        : sensorLocation
                  }
                />
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstallSensorLocation;
